import {B2Cart, B2Occupancy, createApiClient} from "./client";
import {z} from "zod";
import {Instant} from "@variocube/app-ui";
import {notBlank} from "../utils";

export const useSiteApi = () => {
	const api = createApiClient();
	return {
		listSites: async (tenantId: string) =>
			api.get('/api/booking/v2/tenants/{tenantId}/sites', {
				path: { tenantId },
				query: {
					size: 999,
					sort: ['activeFrom', 'name'],
				},
			}),
		getSite: async (tenantId: string, siteId: string) =>
			api.get('/api/booking/v2/tenants/{tenantId}/sites/{siteId}', {
				path: { tenantId, siteId },
			}),
		getAvailability: async (tenantId: string, siteId: string, query: OrderDatesRequest) =>
			api.get('/api/booking/v2/tenants/{tenantId}/sites/{siteId}/availability', {
				path: { tenantId, siteId },
				query
			}),
		getPricing: async (tenantId: string, siteId: string, query: OrderDatesRequest) =>
			api.get('/api/booking/v2/tenants/{tenantId}/sites/{siteId}/pricing', {
				path: { tenantId, siteId },
				query
			}),
		checkDiscount: async (tenantId: string, siteId: string, discountCode: string) =>
			api.get('/api/booking/v2/tenants/{tenantId}/sites/{siteId}/discounts/{discountCode}', {
				path: { tenantId, siteId, discountCode }
			}),
		submitOrder: async (tenantId: string, siteId: string, body: OrderRequest) =>
			api.post('/api/booking/v2/tenants/{tenantId}/sites/{siteId}/bookings', {
				path: { tenantId, siteId },
				body: {
					...body,
					fromDateTime: (notBlank(body.fromDateTime)) ? Instant.from(body.fromDateTime as string).toJSON() : undefined,
					boxes: body.boxes,
				}
			}),
		getBooking: async (bookingId: string) =>
			api.get('/api/booking/v2/bookings/{bookingId}', {
				path: { bookingId },
			}),
		cancelBooking: async (bookingId: string) =>
			api.delete('/api/booking/v2/bookings/{bookingId}', {
				path: { bookingId },
			}),
		prolongBooking: async (bookingId: string, body: BookingProlongRequest) =>
			api.post('/api/booking/v2/bookings/{bookingId}/prolong', {
				path: { bookingId },
				body
			}),
		verifyBookingLocation: async (bookingId: string, cubeId: string) =>
			await api.post('/api/booking/v2/bookings/{bookingId}/cube-verification', {
				path: { bookingId },
				body: { cubeId },
			}) as Promise<BoxAvailability>,
		occupyBoxes: async (bookingId: string, cubeId: string) =>
			api.post('/api/booking/v2/bookings/{bookingId}/occupy', {
				path: { bookingId },
				body: { cubeId },
			}),
		openBox: async (bookingId: string, body: B2Occupancy) =>
			api.post('/api/booking/v2/bookings/{bookingId}/open-box', {
				path: { bookingId },
				body,
			}),
		releaseLockers: async (bookingId: string) =>
			api.post('/api/booking/v2/bookings/{bookingId}/release-lockers', {
				path: { bookingId },
			})
	}
}

type OrderDatesRequest = {
	from?: string;
	until?: string;
	fromDateTime?: string;
	hours?: number;
}

type OrderRequest = B2Cart & {
	boxes: Record<string, number>;
	locale: string;
}

type BookingProlongRequest = {
	until?: string;
	hours?: number;
	discountCode?: string;
}

export type BoxPackagePrice = z.infer<typeof BoxPackagePrice>;
const BoxPackagePrice = z.record(z.number(), z.number());

export type BoxAvailability = z.infer<typeof BoxAvailability>;
const BoxAvailability = z.record(z.string(), z.number());

export type BoxPricing = z.infer<typeof BoxPricing>;
const BoxPricing = z.record(z.string(), z.number());
